import React, { useState, useMemo } from "react";
import { useAppSelector } from "../../../shared/store/hooks";
import { colHeaderShouldNegate } from "../measureRules";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis as RechartsPolarAngleAxis,
  PolarRadiusAxis as RechartsPolarRadiusAxis,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { FONTS, colors } from "../../../shared/ui/tokens";

const PolarAngleAxis = RechartsPolarAngleAxis as any;
const PolarRadiusAxis = RechartsPolarRadiusAxis as any;

export type ChartType = "bar" | "line" | "area" | "pie" | "radar";

// ── AVDASH brand palette ──────────────────────────────────────────────────────
const NW = {
  violet: colors.brand[600],
  violetDark: colors.brand[700],
  violetMed: colors.brand[100],
  violetLight: colors.brand[100],
  violetSoft: colors.neutral[100],
  rose: colors.accent[300],
  roseDark: colors.accent[500],
  roseMed: colors.accent[100],
  white: colors.neutral[0],
  gray50: colors.neutral[50],
  gray100: colors.neutral[100],
  gray200: colors.neutral[200],
  gray300: colors.neutral[300],
  gray500: colors.neutral[500],
  gray700: colors.neutral[700],
  gray800: colors.neutral[800],
  amber: colors.warning[500],
  amberLight: colors.warning[100],
  danger: colors.danger[500],
  dangerLight: colors.danger[50],
  success: colors.accent[500],
  teal: colors.accent[300],
  tealLight: colors.accent[100],
  orange: colors.warning[600],
  orangeLight: colors.warning[50],
};

export const CHART_COLORS = [
  "#2B5CE6", // AVDASH blue
  "#3ECFBF", // AVDASH teal
  "#F59E0B", // amber
  "#EF4444", // red
  "#8B5CF6", // violet
  "#06B6D4", // cyan
  "#F97316", // orange
  "#10B981", // emerald
  "#EC4899", // pink
  "#6366F1", // indigo
];

export const TYPE_OPTIONS: { type: ChartType; icon: string; label: string }[] =
  [
    { type: "bar", icon: "📊", label: "Bar" },
    { type: "line", icon: "📈", label: "Line" },
    { type: "area", icon: "🏔️", label: "Area" },
    { type: "pie", icon: "🥧", label: "Pie" },
    { type: "radar", icon: "🕸️", label: "Radar" },
  ];

export function fmtVal(val: any): number {
  if (val == null || val === "") return 0;
  return typeof val === "number" ? val : parseFloat(val) || 0;
}

/** Détecte les valeurs de dimension/colonne à exclure : vide, "All", "Unknown", "Inconnue"… */
export function isExcludedMember(val: any): boolean {
  const s = String(val ?? "")
    .trim()
    .toLowerCase();
  if (s === "") return true;
  if (s === "all" || s === "unknown" || s === "n/a") return true;
  if (/ - all$/i.test(s) || / - unknown$/i.test(s)) return true;
  if (s.includes("inconnu")) return true; // couvre "inconnu", "inconnue", "couleur inconnue"…
  return false;
}

// ── Style helpers ─────────────────────────────────────────────────────────────
const card = (extra?: React.CSSProperties): React.CSSProperties => ({
  background: NW.white,
  borderRadius: 14,
  border: `1.5px solid ${NW.gray200}`,
  boxShadow: "0 2px 16px rgba(24,75,140,0.07)",
  ...extra,
});

// ── ChartPanel: one independent chart ────────────────────────────────────────
interface ChartPanelProps {
  title: string;
  data: { name: string; value: number }[];
  color?: string;
}

export const ChartPanel: React.FC<ChartPanelProps> = ({
  title,
  data,
  color = NW.violet,
}) => {
  const [type, setType] = useState<ChartType>("bar");

  const tooltipStyle: React.CSSProperties = {
    borderRadius: 10,
    border: `1px solid ${NW.violetMed}`,
    fontFamily: FONTS.body,
    fontSize: 12,
    boxShadow: "0 4px 20px rgba(24,75,140,0.15)",
  };

  const renderChart = () => {
    switch (type) {
      case "bar":
        return (
          <BarChart data={data}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={NW.gray100}
              vertical={false}
            />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 10, fill: NW.gray700, fontFamily: FONTS.body }}
              interval={0}
              angle={-30}
              textAnchor="end"
              height={60}
            />
            <YAxis
              tick={{ fontSize: 11, fill: NW.gray500, fontFamily: FONTS.body }}
            />
            <Tooltip
              formatter={(v: any) =>
                typeof v === "number" ? v.toLocaleString("fr-FR") : v
              }
              contentStyle={tooltipStyle}
            />
            <Bar
              dataKey="value"
              name={title}
              fill={color}
              radius={[5, 5, 0, 0]}
            />
          </BarChart>
        );
      case "line":
        return (
          <LineChart data={data}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={NW.gray100}
              vertical={false}
            />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 10, fill: NW.gray700, fontFamily: FONTS.body }}
              interval={0}
              angle={-30}
              textAnchor="end"
              height={60}
            />
            <YAxis
              tick={{ fontSize: 11, fill: NW.gray500, fontFamily: FONTS.body }}
            />
            <Tooltip
              formatter={(v: any) =>
                typeof v === "number" ? v.toLocaleString("fr-FR") : v
              }
              contentStyle={tooltipStyle}
            />
            <Line
              type="monotone"
              dataKey="value"
              name={title}
              stroke={color}
              dot={false}
              strokeWidth={2.5}
            />
          </LineChart>
        );
      case "area":
        return (
          <AreaChart data={data}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={NW.gray100}
              vertical={false}
            />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 10, fill: NW.gray700, fontFamily: FONTS.body }}
              interval={0}
              angle={-30}
              textAnchor="end"
              height={60}
            />
            <YAxis
              tick={{ fontSize: 11, fill: NW.gray500, fontFamily: FONTS.body }}
            />
            <Tooltip
              formatter={(v: any) =>
                typeof v === "number" ? v.toLocaleString("fr-FR") : v
              }
              contentStyle={tooltipStyle}
            />
            <Area
              type="monotone"
              dataKey="value"
              name={title}
              stroke={color}
              fill={color}
              fillOpacity={0.18}
              strokeWidth={2.5}
            />
          </AreaChart>
        );
      case "pie": {
        const nonZero = data.filter((d) => d.value !== 0);
        return (
          <PieChart>
            <Pie
              data={nonZero}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={110}
              label={({ name, percent }) =>
                `${name} ${((percent ?? 0) * 100).toFixed(0)}%`
              }
              labelLine={false}
            >
              {nonZero.map((_, i) => (
                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(v: any) =>
                typeof v === "number" ? v.toLocaleString("fr-FR") : v
              }
              contentStyle={tooltipStyle}
            />
          </PieChart>
        );
      }
      case "radar":
        return (
          <RadarChart data={data} cx="50%" cy="50%" outerRadius={110}>
            <PolarGrid stroke={NW.gray200} />
            <PolarAngleAxis
              dataKey="name"
              tick={{ fontSize: 10, fontFamily: FONTS.body, fill: NW.gray700 }}
            />
            <PolarRadiusAxis tick={{ fontSize: 9, fontFamily: FONTS.body }} />
            <Radar
              name={title}
              dataKey="value"
              stroke={color}
              fill={color}
              fillOpacity={0.22}
            />
            <Tooltip
              formatter={(v: any) =>
                typeof v === "number" ? v.toLocaleString("fr-FR") : v
              }
              contentStyle={tooltipStyle}
            />
          </RadarChart>
        );
    }
  };

  return (
    <div
      style={card({
        padding: 18,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      })}
    >
      {/* Panel header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: color,
              boxShadow: `0 0 8px ${color}66`,
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontFamily: FONTS.heading,
              fontWeight: 800,
              fontSize: 14,
              color: NW.violetDark,
            }}
          >
            {title}
          </span>
        </div>
        {/* Chart type toggle */}
        <div
          style={{
            display: "flex",
            border: `1.5px solid ${NW.gray200}`,
            borderRadius: 10,
            overflow: "hidden",
          }}
        >
          {TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.type}
              onClick={() => setType(opt.type)}
              title={opt.label}
              style={{
                padding: "4px 10px",
                fontSize: 13,
                border: "none",
                cursor: "pointer",
                background:
                  type === opt.type
                    ? NW.violet
                    : NW.white,
                color: type === opt.type ? "#FFFFFF" : NW.gray500,
                transition: "all 0.15s",
                fontFamily: FONTS.body,
              }}
            >
              {opt.icon}
            </button>
          ))}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        {renderChart() as React.ReactElement}
      </ResponsiveContainer>
    </div>
  );
};

// ── ColumnFilterPanel : sélecteur professionnel avec recherche ────────────────
export interface ColumnFilterPanelProps {
  activeMembers: string[];
  effectiveSelected: Set<string>;
  topN?: number | null;
  onToggle: (member: string) => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
}

export const ColumnFilterPanel: React.FC<ColumnFilterPanelProps> = ({
  activeMembers,
  effectiveSelected,
  topN,
  onToggle,
  onSelectAll,
  onSelectNone,
}) => {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(true);

  const filtered = useMemo(
    () =>
      search.trim() === ""
        ? activeMembers
        : activeMembers.filter((m) =>
            m.toLowerCase().includes(search.trim().toLowerCase()),
          ),
    [activeMembers, search],
  );

  const selectedCount = effectiveSelected.size;
  const totalCount = activeMembers.length;
  const filteredSelectedCount = filtered.filter((m) =>
    effectiveSelected.has(m),
  ).length;

  // Toggle all filtered items
  const handleToggleFiltered = () => {
    const allFilteredSelected = filtered.every((m) => effectiveSelected.has(m));
    if (allFilteredSelected) {
      // deselect all filtered
      filtered.forEach((m) => {
        if (effectiveSelected.has(m)) onToggle(m);
      });
    } else {
      // select all filtered
      filtered.forEach((m) => {
        if (!effectiveSelected.has(m)) onToggle(m);
      });
    }
  };

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((m) => effectiveSelected.has(m));

  return (
    <div
      style={{
        background: NW.white,
        borderRadius: 16,
        border: `1.5px solid ${NW.gray200}`,
        boxShadow: "0 2px 20px rgba(24,75,140,0.08)",
        overflow: "hidden",
      }}
    >
      {/* ── Header ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 18px",
          background: `linear-gradient(90deg, ${NW.violetSoft} 0%, ${NW.white} 100%)`,
          borderBottom: `1px solid ${NW.violetMed}`,
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        {/* Left: title + counter */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            onClick={() => setOpen((v) => !v)}
            title={open ? "Réduire" : "Développer"}
            style={{
              width: 24,
              height: 24,
              borderRadius: 7,
              border: `1.5px solid ${NW.violetMed}`,
              background: NW.white,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 10,
              color: NW.violet,
              fontWeight: 800,
              flexShrink: 0,
              transition: "all 0.15s",
            }}
          >
            {open ? "▲" : "▼"}
          </button>
          <span
            style={{
              fontFamily: FONTS.heading,
              fontWeight: 700,
              fontSize: 12,
              color: NW.violet,
              textTransform: "uppercase",
              letterSpacing: 1.5,
            }}
          >
            ◈ Colonnes
          </span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: NW.violet,
              background: NW.violetLight,
              border: `1px solid ${NW.violetMed}`,
              borderRadius: 99,
              padding: "2px 10px",
              fontFamily: FONTS.body,
            }}
          >
            {selectedCount} / {totalCount}
          </span>
          {topN && (
            <span
              style={{
                fontSize: 11,
                color: NW.amber,
                fontWeight: 700,
                background: NW.amberLight,
                border: `1px solid ${NW.amber}44`,
                borderRadius: 99,
                padding: "2px 10px",
                fontFamily: FONTS.body,
              }}
            >
              🏆 Top {topN}
            </span>
          )}
        </div>

        {/* Right: Tout / Aucun */}
        <div style={{ display: "flex", gap: 6 }}>
          <button
            onClick={onSelectAll}
            style={{
              fontSize: 11,
              padding: "4px 14px",
              border: `1.5px solid ${NW.violet}44`,
              borderRadius: 8,
              cursor: "pointer",
              background: NW.violetSoft,
              color: NW.violet,
              fontWeight: 700,
              fontFamily: FONTS.body,
              transition: "all 0.12s",
            }}
          >
            Tout
          </button>
          <button
            onClick={onSelectNone}
            style={{
              fontSize: 11,
              padding: "4px 14px",
              border: `1.5px solid ${NW.gray200}`,
              borderRadius: 8,
              cursor: "pointer",
              background: NW.white,
              color: NW.gray500,
              fontWeight: 600,
              fontFamily: FONTS.body,
              transition: "all 0.12s",
            }}
          >
            Aucun
          </button>
        </div>
      </div>

      {/* ── Collapsible body ── */}
      {open && (
        <div
          style={{
            padding: "14px 18px",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          {/* Search bar */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: NW.gray50,
              border: `1.5px solid ${NW.gray200}`,
              borderRadius: 10,
              padding: "7px 13px",
              transition: "border-color 0.15s",
            }}
            onFocus={(e) =>
              ((e.currentTarget as HTMLElement).style.borderColor = NW.violet)
            }
            onBlur={(e) =>
              ((e.currentTarget as HTMLElement).style.borderColor = NW.gray200)
            }
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke={NW.gray500}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ flexShrink: 0 }}
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder={`Rechercher parmi ${totalCount} colonnes…`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                flex: 1,
                border: "none",
                background: "transparent",
                outline: "none",
                fontSize: 12,
                color: NW.gray800,
                fontFamily: FONTS.body,
              }}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  background: NW.gray300,
                  border: "none",
                  cursor: "pointer",
                  fontSize: 11,
                  color: NW.white,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 800,
                  flexShrink: 0,
                }}
              >
                ×
              </button>
            )}
          </div>

          {/* Results count + select-all-filtered */}
          {search.trim() !== "" && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  color: NW.gray500,
                  fontFamily: FONTS.body,
                }}
              >
                {filtered.length} résultat{filtered.length !== 1 ? "s" : ""} ·{" "}
                {filteredSelectedCount} sélectionné
                {filteredSelectedCount !== 1 ? "s" : ""}
              </span>
              <button
                onClick={handleToggleFiltered}
                style={{
                  fontSize: 11,
                  padding: "3px 12px",
                  border: `1.5px solid ${NW.violet}44`,
                  borderRadius: 8,
                  cursor: "pointer",
                  background: allFilteredSelected
                    ? NW.violetLight
                    : NW.violetSoft,
                  color: NW.violet,
                  fontWeight: 700,
                  fontFamily: FONTS.body,
                }}
              >
                {allFilteredSelected
                  ? "Désélectionner les résultats"
                  : "Sélectionner les résultats"}
              </button>
            </div>
          )}

          {/* List */}
          <div
            style={{
              maxHeight: 260,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              // Scrollbar styling
              scrollbarWidth: "thin",
              scrollbarColor: `${NW.violetMed} transparent`,
            }}
          >
            {filtered.length === 0 ? (
              <div
                style={{
                  padding: "20px 0",
                  textAlign: "center",
                  fontSize: 12,
                  color: NW.gray500,
                  fontStyle: "italic",
                  fontFamily: FONTS.body,
                }}
              >
                Aucune colonne correspondante
              </div>
            ) : (
              filtered.map((member, i) => {
                const active = effectiveSelected.has(member);
                const dotColor =
                  CHART_COLORS[
                    activeMembers.indexOf(member) % CHART_COLORS.length
                  ];
                return (
                  <div
                    key={member}
                    onClick={() => onToggle(member)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "7px 10px",
                      borderRadius: 9,
                      cursor: "pointer",
                      background: active ? `${dotColor}0D` : "transparent",
                      border: `1px solid ${active ? dotColor + "33" : "transparent"}`,
                      transition: "all 0.12s",
                      userSelect: "none",
                    }}
                    onMouseEnter={(e) => {
                      if (!active)
                        (e.currentTarget as HTMLElement).style.background =
                          NW.gray50;
                    }}
                    onMouseLeave={(e) => {
                      if (!active)
                        (e.currentTarget as HTMLElement).style.background =
                          "transparent";
                    }}
                  >
                    {/* Custom checkbox */}
                    <div
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: 5,
                        border: `2px solid ${active ? dotColor : NW.gray300}`,
                        background: active ? dotColor : NW.white,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        transition: "all 0.12s",
                      }}
                    >
                      {active && (
                        <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                          <polyline
                            points="1,3.5 3.5,6 8,1"
                            stroke="#FFFFFF"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>

                    {/* Color dot */}
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: dotColor,
                        opacity: active ? 1 : 0.35,
                        flexShrink: 0,
                        transition: "opacity 0.12s",
                      }}
                    />

                    {/* Label */}
                    <span
                      style={{
                        fontSize: 12,
                        color: active ? NW.gray800 : NW.gray500,
                        fontWeight: active ? 600 : 400,
                        fontFamily: FONTS.body,
                        flex: 1,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        transition: "color 0.12s",
                      }}
                    >
                      {member}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── PeriodDashboard ───────────────────────────────────────────────────────────
interface PeriodDashboardProps {
  data: { columns: string[]; rows: any[][] };
  rowDimCount: number;
  accentColor?: string;
}

export const PeriodDashboard: React.FC<{
  data: { columns: string[]; rows: any[][] };
  rowDimCount: number;
  accentColor?: string;
  topN?: number | null;
}> = ({ data, rowDimCount, accentColor = NW.violet, topN: topNProp }) => {
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const topNFromStore = useAppSelector((s) => s.query.topN);
  const topN = topNProp !== undefined ? topNProp : topNFromStore;
  const allMeasures = useAppSelector((s) => s.cube.measures);

  const rowCols = Math.min(rowDimCount, data.columns.length);
  const rowLabelIdx = rowCols > 0 ? rowCols - 1 : 0;
  const columnMembers = data.columns.slice(rowCols);

  const nonEmptyMembers = new Set<string>();
  data.rows.forEach((row) => {
    columnMembers.forEach((member, i) => {
      const val = row[rowCols + i];
      if (val != null && val !== "" && val !== 0) nonEmptyMembers.add(member);
    });
  });

  const activeMembers = columnMembers.filter(
    (m) => nonEmptyMembers.has(m) && !isExcludedMember(m),
  );

  // ── Cas rôle restreint (vendeur / responsable boutique) ──────────────────
  // Quand rowDimCount = 0, il n'y a pas de columnMembers croisés : les colonnes
  // de data.columns SONT directement les mesures. On les utilise comme membres.
  const directMembers = useMemo(() => {
    if (activeMembers.length > 0 || columnMembers.length > 0) return [];
    // Toutes les colonnes sauf celles qui représentent des labels exclus
    return data.columns.filter((c) => !isExcludedMember(c));
  }, [activeMembers.length, columnMembers.length, data.columns]);

  const resolvedMembers =
    activeMembers.length > 0 ? activeMembers : directMembers;

  const effectiveSelected =
    selected === null ? new Set(resolvedMembers) : selected;

  const toggle = (val: string) => {
    const base =
      selected === null ? new Set(resolvedMembers) : new Set(selected);
    if (base.has(val)) base.delete(val);
    else base.add(val);
    setSelected(base);
  };

  if (!data.rows.length) {
    return (
      <div
        style={{
          color: NW.gray500,
          padding: 40,
          textAlign: "center",
          fontSize: 13,
          background: NW.white,
          borderRadius: 14,
          border: `1.5px dashed ${NW.violetMed}`,
          fontFamily: FONTS.body,
        }}
      >
        Aucun résultat.
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 14,
        fontFamily: FONTS.body,
      }}
    >
      {/* ── Professional Column Filter Panel ── */}
      {resolvedMembers.length > 0 && (
        <ColumnFilterPanel
          activeMembers={resolvedMembers}
          effectiveSelected={effectiveSelected}
          topN={topN}
          onToggle={toggle}
          onSelectAll={() => setSelected(null)}
          onSelectNone={() => setSelected(new Set())}
        />
      )}

      {/* Empty state */}
      {effectiveSelected.size === 0 && resolvedMembers.length > 0 ? (
        <div
          style={{
            color: NW.gray500,
            fontSize: 13,
            padding: 20,
            textAlign: "center",
            fontFamily: FONTS.body,
          }}
        >
          Sélectionnez au moins une colonne.
        </div>
      ) : effectiveSelected.size === 0 && resolvedMembers.length === 0 ? (
        <div
          style={{
            color: NW.gray500,
            fontSize: 13,
            padding: 20,
            textAlign: "center",
            fontFamily: FONTS.body,
          }}
        >
          Aucune mesure disponible pour ce rôle.
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(480px, 1fr))",
            gap: 16,
          }}
        >
          {resolvedMembers
            .filter((m) => effectiveSelected.has(m))
            .map((member, i) => {
              // Pour le mode direct (rôle restreint), colIdx dans data.columns
              const colIdx =
                activeMembers.length > 0
                  ? columnMembers.indexOf(member)
                  : data.columns.indexOf(member);
              const negate = colHeaderShouldNegate(member, allMeasures);
              const color = accentColor;
              let chartData = data.rows
                .filter((row) => {
                  // ✅ Supprimer all/unknown/inconnue/vide sur toutes les colonnes de dimension
                  for (let di = 0; di < Math.max(rowCols, 1); di++) {
                    if (isExcludedMember(row[di])) return false;
                  }
                  return true;
                })
                .map((row) => {
                  // En mode direct, rowCols = 0, on lit row[colIdx] directement
                  const raw =
                    activeMembers.length > 0
                      ? fmtVal(row[rowCols + colIdx])
                      : fmtVal(row[colIdx]);
                  return {
                    name: String(row[rowLabelIdx] ?? ""),
                    value: negate ? raw * -1 : raw,
                  };
                })
                .filter((d) => d.value !== 0)
                .sort((a, b) => b.value - a.value);
              if (topN) chartData = chartData.slice(0, topN);
              return (
                <ChartPanel
                  key={member}
                  title={member}
                  data={chartData}
                  color={color}
                />
              );
            })}
        </div>
      )}
    </div>
  );
};
